import React, {useEffect} from 'react';
import {
	DirectionalHint,
	FontIconCompat as FontIcon,
	StackCompat as Stack,
	TooltipDelay,
	TooltipHostCompat as TooltipHost,
	useTheme,
	TextCompat as Text,
} from 'components/FluentUICompat';
import {
	LazyFieldRenderInput,
	renderArrayField,
	RequirementOfRequirementsField,
} from './ColumnRenderers';
import {useTranslation} from 'react-i18next';
import {useRequirementsTranslation} from './RequirementsFields/useRequirementsTranslation';
import {RequirementsFieldName} from './RequirementsFields/RequirementsFieldName';
import {RichtextDisplay} from 'components/RichtextDisplay';
import {VexClustersField} from './RequirementsFields/VexClustersField';
import {LoadSpinner} from 'components/LoadWrapper/LoadSpinner';
import {useGetRequirementTooltipDetailsLazyQuery} from './hooks/useGetRequirementTooltipDetails.generated';
import {createHrefFromRequirement} from 'features/Requirements/requirementLinks.utils';
import {getStatusTag} from 'components/StatusTag';

export const RequirementsField: React.FC<LazyFieldRenderInput> = ({
	item,
	i,
	col,
	isLazy,
}) => {
	const theme = useTheme();
	const {t: tEnum} = useTranslation('common/enums', {
		keyPrefix: 'RequirementStatus',
	});
	const {t} = useRequirementsTranslation();

	const [getRequirementTooltipDetails, {data, loading}] =
		useGetRequirementTooltipDetailsLazyQuery();

	const [loadedReqs, setLoadedReqs] = React.useState<
		RequirementOfRequirementsField[]
	>([]);

	useEffect(() => {
		if (!data?.requirement?.id) {
			return;
		}
		if (!loadedReqs.find(req => req.id === data.requirement!.id)) {
			setLoadedReqs([...loadedReqs, data.requirement]);
		}
	}, [data, loadedReqs]);

	const loadedTooltipContent = React.useCallback(
		(renderItem: RequirementOfRequirementsField) => {
			return (
				<Stack>
					<Stack horizontal>
						<RequirementsFieldName>{t('Title')}:</RequirementsFieldName>
						<a
							href={createHrefFromRequirement(renderItem)}
							target='_blank'
							rel='noreferrer'
						>
							<Text>
								{renderItem.name} <FontIcon iconName='OpenInNewWindow' />
							</Text>
						</a>
					</Stack>
					<Stack horizontal>
						<RequirementsFieldName>{t('Status')}:</RequirementsFieldName>
						<Text>{getStatusTag(renderItem.status, theme, tEnum)}</Text>
					</Stack>
					<Stack horizontal>
						<RequirementsFieldName>{t('RequirementId')}:</RequirementsFieldName>
						<Text>{renderItem.requirementId || '-'}</Text>
					</Stack>
					<Stack horizontal>
						<RequirementsFieldName>{t('Definition')}:</RequirementsFieldName>
						<Text>
							<RichtextDisplay
								removeHtml
								value={renderItem.definition || '-'}
								limit={200}
							/>
						</Text>
					</Stack>
					<VexClustersField clusters={renderItem.vexClusters} />
				</Stack>
			);
		},
		[],
	);

	const renderRequirementWithTooltip = (
		requirement: RequirementOfRequirementsField,
	) => {
		const loadedReq = loadedReqs.find(req => req.id === requirement.id);
		const renderItem = loadedReq ?? requirement;
		return (
			<TooltipHost
				key={`${i}`}
				onTooltipToggle={(isTooltipVisible: boolean) => {
					if (
						isTooltipVisible &&
						!loadedReqs.find(req => req.id === requirement.id)
					) {
						getRequirementTooltipDetails({
							variables: {
								requirementId: requirement.id,
							},
						});
					}
				}}
				tooltipProps={{
					onRenderContent: () =>
						isLazy && !loadedReq ? (
							<LoadSpinner
								extraStyles={{root: {height: '60px', top: '30px'}}}
							/>
						) : (
							loadedTooltipContent(renderItem)
						),
				}}
				delay={TooltipDelay.zero}
				id={`reqToolTip-${renderItem.id}-${item.id}`}
				directionalHint={DirectionalHint.leftCenter}
			>
				{renderItem.name}
			</TooltipHost>
		);
	};
	return renderArrayField(renderRequirementWithTooltip)(item, i, col);
};
